import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import {
  PreviewConnectionStatus,
  PreviewVariant,
  useSendPreview,
} from "../components/hooks/useSendPreview"

type PreviewRegistration = {
  preview: PreviewVariant | null
  debounceMs: number
  order: number
}

type LivePreviewContextValue = {
  registerPreview: (ownerId: string, preview: PreviewVariant | null, debounceMs: number) => void
  unregisterPreview: (ownerId: string) => void
  previewConnectionStatus: PreviewConnectionStatus
  previewConnectionMessage: string
  lastCheckedAt: number | null
}

const ROUTE_HANDOFF_GRACE_MS = 125

const LivePreviewContext = createContext<LivePreviewContextValue | null>(null)

export const LivePreviewProvider = ({ children }: { children: ReactNode }) => {
  const {
    sendPreview,
    previewConnectionStatus,
    previewConnectionMessage,
    lastCheckedAt,
  } = useSendPreview()
  const registrationsRef = useRef(new Map<string, PreviewRegistration>())
  const registrationOrderRef = useRef(0)
  const appliedPreviewRef = useRef<PreviewVariant | null>(null)
  const appliedSignatureRef = useRef<string | null>(null)
  const operationRevisionRef = useRef(0)
  const applyTimeoutRef = useRef<number | null>(null)
  const clearTimeoutRef = useRef<number | null>(null)

  const clearScheduledWork = useCallback(() => {
    if (applyTimeoutRef.current !== null) {
      window.clearTimeout(applyTimeoutRef.current)
      applyTimeoutRef.current = null
    }
    if (clearTimeoutRef.current !== null) {
      window.clearTimeout(clearTimeoutRef.current)
      clearTimeoutRef.current = null
    }
  }, [])

  const applyPreview = useCallback(async (nextPreview: PreviewVariant | null) => {
    const revision = ++operationRevisionRef.current
    const nextSignature = nextPreview ? JSON.stringify(nextPreview) : null

    if (nextSignature === appliedSignatureRef.current) return

    const appliedPreview = appliedPreviewRef.current
    if (!nextPreview) {
      if (!appliedPreview) return

      appliedPreviewRef.current = null
      appliedSignatureRef.current = null
      await sendPreview({ type: "variant-preview", action: "clear", data: appliedPreview })
      return
    }

    const isDifferentVariant = Boolean(
      appliedPreview
      && (
        appliedPreview.experiment_id !== nextPreview.experiment_id
        || appliedPreview.variant_id !== nextPreview.variant_id
      )
    )

    if (isDifferentVariant && appliedPreview) {
      appliedPreviewRef.current = null
      appliedSignatureRef.current = null
      await sendPreview({ type: "variant-preview", action: "clear", data: appliedPreview })
      if (revision !== operationRevisionRef.current) return
    }

    appliedPreviewRef.current = nextPreview
    appliedSignatureRef.current = nextSignature
    await sendPreview({ type: "variant-preview", data: nextPreview })
  }, [sendPreview])

  const reconcilePreview = useCallback(() => {
    clearScheduledWork()

    const registrations = Array.from(registrationsRef.current.values())
    const activeRegistration = registrations.reduce<PreviewRegistration | null>(
      (latest, registration) => !latest || registration.order > latest.order ? registration : latest,
      null,
    )

    if (!activeRegistration) {
      clearTimeoutRef.current = window.setTimeout(() => {
        clearTimeoutRef.current = null
        void applyPreview(null)
      }, ROUTE_HANDOFF_GRACE_MS)
      return
    }

    applyTimeoutRef.current = window.setTimeout(() => {
      applyTimeoutRef.current = null
      void applyPreview(activeRegistration.preview)
    }, activeRegistration.debounceMs)
  }, [applyPreview, clearScheduledWork])

  const registerPreview = useCallback((
    ownerId: string,
    preview: PreviewVariant | null,
    debounceMs: number,
  ) => {
    const existing = registrationsRef.current.get(ownerId)
    registrationsRef.current.set(ownerId, {
      preview,
      debounceMs,
      order: existing?.order ?? ++registrationOrderRef.current,
    })
    reconcilePreview()
  }, [reconcilePreview])

  const unregisterPreview = useCallback((ownerId: string) => {
    registrationsRef.current.delete(ownerId)
    reconcilePreview()
  }, [reconcilePreview])

  useEffect(() => () => {
    clearScheduledWork()
    operationRevisionRef.current += 1
    const appliedPreview = appliedPreviewRef.current
    if (appliedPreview) {
      void sendPreview({ type: "variant-preview", action: "clear", data: appliedPreview })
    }
  }, [clearScheduledWork, sendPreview])

  const value = useMemo<LivePreviewContextValue>(() => ({
    registerPreview,
    unregisterPreview,
    previewConnectionStatus,
    previewConnectionMessage,
    lastCheckedAt,
  }), [
    lastCheckedAt,
    previewConnectionMessage,
    previewConnectionStatus,
    registerPreview,
    unregisterPreview,
  ])

  return <LivePreviewContext.Provider value={value}>{children}</LivePreviewContext.Provider>
}

export const useLivePreviewContext = (): LivePreviewContextValue => {
  const context = useContext(LivePreviewContext)
  if (!context) {
    throw new Error("useLivePreview must be used within a LivePreviewProvider")
  }
  return context
}
